<script setup lang="ts">
import { reactive } from 'vue';
import {
  Button,
  Form,
  FormItem,
  Upload,
} from 'ant-design-vue';
import type { UploadFile } from 'ant-design-vue/lib/upload/interface';
import type { CombinedError } from '@urql/vue';
import { UploadOutlined } from '@ant-design/icons-vue';

import AlertError from '@/ui/NoDataUrql/AlertError.vue';

interface Props {
  fetching?: boolean;
  error?: CombinedError;
}

defineProps<Props>();

const emit = defineEmits<{
  submit: [file: File];
  cancel: [];
}>();

type FormState = {
  fileList: UploadFile[];
}

const formState = reactive<FormState>({ fileList: [] });
const onFormFinish = (values: FormState) => {
  const raw = values.fileList[0]?.originFileObj;
  if (!raw) {
    throw new Error('File not found');
  }
  emit('submit', raw);
};

const beforeUpload = (file: UploadFile) => {
  formState.fileList = [file];
  return false;
};
const onRemove = () => {
  formState.fileList = [];
};
const onCancel = () => { emit('cancel'); };
</script>

<template>
  <Form
    layout="vertical"
    :model="formState"
    @finish="onFormFinish"
  >
    <FormItem
      label="File"
      name="fileList"
      :rules="[
        {
          required: true,
          type: 'array' as const,
          min: 1,
          message: 'Please select a file',
        },
      ]"
    >
      <Upload
        v-model:file-list="formState.fileList"
        :before-upload="beforeUpload"
        :max-count="1"
        @remove="onRemove"
      >
        <Button>
          <UploadOutlined />
          Choose file
        </Button>
      </Upload>
    </FormItem>
    <AlertError
      v-if="error"
      :error="error"
      class="mb-4"
    />
    <FormItem>
      <div class="flex justify-end gap-2">
        <Button @click="onCancel">
          Cancel
        </Button>
        <Button
          type="primary"
          html-type="submit"
          :loading="fetching"
        >
          Upload
        </Button>
      </div>
    </FormItem>
  </Form>
</template>
